import React, { useEffect, useState } from "react";
import { Box, Typography, CircularProgress, Stack } from "@mui/material";
import { Article } from "@mui/icons-material";
import { RequestFile } from "models/interfaces";
import {
  fetchDocumentSummary,
  fetchDocumentSummaryCallback,
} from "../SignRequestService";
import { handleErrorMessage } from "helpers/methods";
import { AppModal } from "global/components/AppModal";
import { convertFileForBackend } from "screens/Shared/CommonHelper";

const formatLabel = (key: string) =>
  key
    .split("_")
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(" ");

const ContractAtAGlanceDialog = ({
  open,
  onClose,
  envelopeId,
  selectedFile,
  isPublic,
  requestToken,
}: {
  open: boolean;
  onClose: () => void;
  envelopeId: string;
  selectedFile: RequestFile | null;
  isPublic?: boolean;
  requestToken?: string;
}) => {
  const [data, setData] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !selectedFile) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setData(null);
        const file = convertFileForBackend(selectedFile);
        const res: any = await (isPublic
          ? fetchDocumentSummaryCallback(requestToken!, file)
          : fetchDocumentSummary(envelopeId, file));
        if (!cancelled && res) {
          // Core sends the glance as `contractAtGlance`; keep the older
          // snake-case spelling as a fallback.
          setData(res.contractAtGlance ?? res.contract_at_a_glance ?? null);
        }
      } catch (e: any) {
        if (!cancelled) handleErrorMessage(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, selectedFile, envelopeId, isPublic, requestToken]);

  const renderValue = (value: any) => {
    if (value === null || value === undefined || value === "") {
      return (
        <Typography
          variant="body2"
          sx={{ color: "#646464", fontStyle: "italic", fontSize: "0.875rem" }}
        >
          Not specified
        </Typography>
      );
    }
    if (Array.isArray(value)) {
      return (
        <Box component="ul" sx={{ pl: 2.5, m: 0, listStyle: "none" }}>
          {value.map((item, idx) => (
            <Box
              component="li"
              key={idx}
              sx={{
                mb: 0.5,
                pl: 1.5,
                position: "relative",
                "&::before": {
                  content: '"•"',
                  position: "absolute",
                  left: 0,
                  color: "#646464",
                  fontSize: "1.2rem",
                  lineHeight: "1.5",
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontSize: "0.875rem", color: "#444" }}
              >
                {typeof item === "object" ? JSON.stringify(item) : String(item)}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
    return (
      <Typography
        variant="body2"
        sx={{ fontSize: "0.875rem", color: "#444", lineHeight: 1.6 }}
      >
        {String(value)}
      </Typography>
    );
  };

  const body = (
    <Box sx={{ minHeight: 200, textAlign: "left" }}>
      {loading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
          <CircularProgress size={28} />
          <Typography variant="body2" sx={{ mt: 2, color: "#646464" }}>
            Analyzing document...
          </Typography>
        </Stack>
      ) : data ? (
        <Stack spacing={2}>
          {Object.entries(data).map(([key, value]) => (
            <Box key={key}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  color: "text.primary",
                  mb: 0.5,
                }}
              >
                {formatLabel(key)}
              </Typography>
              {renderValue(value)}
            </Box>
          ))}
        </Stack>
      ) : (
        <Typography
          variant="body2"
          sx={{ textAlign: "center", color: "#646464", py: 4 }}
        >
          No contract details available.
        </Typography>
      )}
    </Box>
  );

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title="Contract at a Glance"
      // Keeps the document glyph the dialog has always shown, inside the
      // system's icon tile.
      icon={<Article sx={{ fontSize: 19 }} />}
      tone="neutral"
      size="md"
    >
      {body}
    </AppModal>
  );
};

export default ContractAtAGlanceDialog;
